
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { usePortfolioUpdates } from './usePortfolioUpdates';

interface PortfolioItem {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  image_url?: string;
  project_url?: string;
  created_at: string;
  updated_at: string;
}

export const usePortfolio = (userId?: string) => {
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { portfolioVersion } = usePortfolioUpdates();

  const fetchPortfolio = async () => {
    if (!userId) {
      setPortfolioItems([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('portfolio_items')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPortfolioItems(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching portfolio:', err);
      setError('Failed to load portfolio items');
    } finally {
      setLoading(false);
    }
  };

  const deletePortfolioItem = async (itemId: string) => {
    try {
      const { error } = await supabase
        .from('portfolio_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      setPortfolioItems(prev => prev.filter(item => item.id !== itemId));
      toast.success('Portfolio item deleted successfully');
    } catch (err) {
      console.error('Error deleting portfolio item:', err);
      toast.error('Failed to delete portfolio item');
    }
  };

  // Fetch portfolio initially and when portfolioVersion changes
  useEffect(() => {
    fetchPortfolio();
  }, [userId, portfolioVersion]);

  return {
    portfolioItems,
    loading,
    error,
    refetch: fetchPortfolio,
    deletePortfolioItem
  };
};
